import { useMemo } from 'react';

import { resolveIsOwner } from '@/utils/permissions';
import type { CatalogFilterKey, SixCatalogItem } from '@/types/Sixes';
import type { SortKey } from '@/types/SixCatalog.types';

const RECENT_WINDOW_DAYS = 7;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const RECENT_WINDOW_MS =
  RECENT_WINDOW_DAYS *
  HOURS_PER_DAY *
  MINUTES_PER_HOUR *
  SECONDS_PER_MINUTE *
  MILLISECONDS_PER_SECOND;

interface UseClientCatalogFilteringParams {
  sixes: SixCatalogItem[];
  searchValue: string;
  activeFilters: CatalogFilterKey[];
  currentUserId: string | undefined;
  selectedSortKey: SortKey;
}

export function useClientCatalogFiltering({
  sixes,
  searchValue,
  activeFilters,
  currentUserId,
  selectedSortKey,
}: UseClientCatalogFilteringParams): SixCatalogItem[] {
  return useMemo<SixCatalogItem[]>(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const now = Date.now();

    const passesFilter = (six: SixCatalogItem, filter: CatalogFilterKey): boolean => {
      switch (filter) {
        case 'favorites':
          return six.isFavorite;
        case 'mySixes':
          return resolveIsOwner(currentUserId, six.owners);
        case 'recent':
          return now - six.lastModified.getTime() <= RECENT_WINDOW_MS;
      }
    };

    const filtered = sixes.filter((six) => {
      const matchesSearch =
        normalizedSearch.length === 0
          ? true
          : `${six.name} ${six.description}`.toLowerCase().includes(normalizedSearch);

      const matchesFilters = activeFilters.every((filter) => passesFilter(six, filter));

      // Temporary stopgap: local search + local filters with AND semantics.
      return matchesSearch && matchesFilters;
    });

    const sorted = [...filtered].sort((left, right) => {
      switch (selectedSortKey) {
        case 'lastActivity': {
          return right.lastModified.getTime() - left.lastModified.getTime();
        }
        case 'name': {
          return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
        }
        case 'members': {
          return left.participantCount - right.participantCount;
        }
        case 'files': {
          return left.fileCount - right.fileCount;
        }
      }
    });

    return sorted;
  }, [activeFilters, currentUserId, searchValue, selectedSortKey, sixes]);
}
